/**
 * The admin area's one period (Dustin, 2026-10-07): every page that shows a span of time shows the same
 * one, chosen once. Days are UTC days, `yyyy-MM-dd`, and a period is inclusive at both ends, the way the
 * API's `from` and `until` are. Pure, so every rule is tested without a clock or a DOM.
 */

export type PeriodPreset = 'today' | '7d' | '30d' | 'month' | 'last-month' | 'custom';

export type PeriodChoice =
  { preset: Exclude<PeriodPreset, 'custom'> } | { preset: 'custom'; from: string; until: string };

/** A span of UTC days, both inclusive. */
export interface Period {
  from: string;
  until: string;
}

/** The presets the picker offers, in the order it shows them. */
export const periodPresets = ['today', '7d', '30d', 'month', 'last-month'] as const;

/** The API's limit: `until` at most this many days after `from`. */
export const MAX_PERIOD_DAYS = 731;

const DAY = /^\d{4}-\d{2}-\d{2}$/;
const DAY_MS = 86_400_000;

const stamp = (day: string): number => Date.parse(`${day}T00:00:00Z`);
const dayOf = (ms: number): string => new Date(ms).toISOString().slice(0, 10);

/** A real calendar day written yyyy-MM-dd: "2026-02-30" is not one. */
export function isDay(text: string | undefined): text is string {
  if (text === undefined || !DAY.test(text)) return false;
  const at = stamp(text);
  return Number.isFinite(at) && dayOf(at) === text;
}

/** The UTC day it is now. */
export function todayUtc(now: Date = new Date()): string {
  return now.toISOString().slice(0, 10);
}

/** The day `n` days after `day` (before, when negative). */
export function addDays(day: string, n: number): string {
  return dayOf(stamp(day) + n * DAY_MS);
}

/** How many days a period holds, both ends counted. */
export function daysIn(period: Period): number {
  return Math.round((stamp(period.until) - stamp(period.from)) / DAY_MS) + 1;
}

/** A custom pair the API takes: two real days, in order, no longer than the limit. */
export function validSpan(from: string | undefined, until: string | undefined): boolean {
  if (!isDay(from) || !isDay(until) || until < from) return false;
  return daysIn({ from, until }) - 1 <= MAX_PERIOD_DAYS;
}

function lastMonth(todayDay: string): Period {
  const first = `${todayDay.slice(0, 8)}01`;
  const until = addDays(first, -1);
  return { from: `${until.slice(0, 8)}01`, until };
}

/** The days a choice means on `todayDay`: 7d is today and the six days before it. */
export function resolvePeriod(choice: PeriodChoice, todayDay: string): Period {
  switch (choice.preset) {
    case 'custom':
      return { from: choice.from, until: choice.until };
    case 'today':
      return { from: todayDay, until: todayDay };
    case '7d':
      return { from: addDays(todayDay, -6), until: todayDay };
    case '30d':
      return { from: addDays(todayDay, -29), until: todayDay };
    case 'month':
      return { from: `${todayDay.slice(0, 8)}01`, until: todayDay };
    case 'last-month':
      return lastMonth(todayDay);
  }
}

const WORDS: Record<PeriodPreset, string> = {
  today: 'Today',
  '7d': 'Last 7 days',
  '30d': 'Last 30 days',
  month: 'This month',
  'last-month': 'Last month',
  custom: 'Custom',
};

/** The preset as the picker names it. */
export function presetWord(preset: PeriodPreset): string {
  return WORDS[preset];
}

function dayWord(day: string, locale: string | undefined, withYear: boolean): string {
  return new Intl.DateTimeFormat(locale, {
    month: 'short',
    day: 'numeric',
    timeZone: 'UTC',
    ...(withYear ? { year: 'numeric' } : {}),
  }).format(stamp(day));
}

/**
 * "Oct 1 to Oct 7"; one day reads "Oct 7". The year is said only when a day is not in this year
 * (`todayDay`'s, the clock's when absent), and then on both days.
 */
export function periodLabel(
  period: Period,
  locale?: string,
  todayDay: string = todayUtc(),
): string {
  const year = todayDay.slice(0, 4);
  const withYear = period.from.slice(0, 4) !== year || period.until.slice(0, 4) !== year;
  const until = dayWord(period.until, locale, withYear);
  if (period.from === period.until) return until;
  return `${dayWord(period.from, locale, withYear)} to ${until}`;
}

/**
 * What a tile calls its number, so every figure states its period: "Pages read, last 7 days", or
 * "Pages read, Sep 1 to Sep 30" for a custom span.
 */
export function tileLabel(
  what: string,
  choice: PeriodChoice,
  period: Period,
  locale?: string,
): string {
  const when =
    choice.preset === 'custom'
      ? periodLabel(period, locale)
      : presetWord(choice.preset).toLowerCase();
  return `${what}, ${when}`;
}

/** The period as the URL holds it. */
export interface PeriodSearch {
  period?: string | undefined;
  from?: string | undefined;
  until?: string | undefined;
}

const isPreset = (text: string): text is Exclude<PeriodPreset, 'custom'> =>
  (periodPresets as readonly string[]).includes(text);

/** The choice a URL names: `?from=&until=` first, then `?period=`; null when it names none that is valid. */
export function parsePeriod(search: PeriodSearch): PeriodChoice | null {
  if (search.from !== undefined || search.until !== undefined) {
    return validSpan(search.from, search.until)
      ? { preset: 'custom', from: search.from as string, until: search.until as string }
      : null;
  }
  return search.period !== undefined && isPreset(search.period) ? { preset: search.period } : null;
}

/** The URL for a choice: `?period=30d`, or `?from=2026-09-01&until=2026-09-30` for a custom span. */
export function searchOf(choice: PeriodChoice): PeriodSearch {
  return choice.preset === 'custom'
    ? { from: choice.from, until: choice.until }
    : { period: choice.preset };
}
